import createFetchClient from "openapi-fetch";
import type { paths } from "./generated/schema";
import {
  authMiddleware,
  correlationMiddleware,
  errorMiddleware,
  localeMiddleware,
  createRefreshMiddleware,
} from "./middleware";
import { billingMiddleware } from "./middleware/billing";
import type { TokenProvider } from "./auth-bridge/tokenProvider";
import { ApiError, type ApiErrorPayload } from "./errors/ApiError";
import type { ErrorCode } from "./errors/errorCodes";

export interface CreateApiClientOptions {
  /** Absolute origin (e.g. `https://api.hr.linkco.jo`) or dev proxy base. */
  baseUrl: string;
  /** Provided by `@linkhr/auth` (Phase 1). Use `nullTokenProvider` for pre-auth tests. */
  tokenProvider: TokenProvider;
  /**
   * Phase 2 — billing-warning setter. When provided, billingMiddleware
   * registers on every response and invokes setter with header value
   * (or null on clear). Mirrors tokenProvider injection pattern.
   * Skipped on `/api/v1/_platform/` URLs (Link Admin) per D-20.
   */
  setBillingWarning?: (severity: string | null) => void;
  /** For unit tests only. Production should use the default `globalThis.fetch`. */
  fetch?: (input: Request) => Promise<Response>;
}

/**
 * Wrap a client method so 401 → single-flight refresh → retry applies uniformly.
 * Preserves the original method's full type signature (including inferred
 * path/body/response generics).
 */
type Wrap = <Fn extends (...args: never[]) => Promise<unknown>>(fn: Fn) => Fn;

/**
 * Creates a fully-wired API client:
 *   - Typed from the generated OpenAPI `paths`
 *   - `authMiddleware`  — injects Bearer token on every request
 *   - `correlationMiddleware` — reads `X-Correlation-Id` → Sentry + OTel span
 *   - `errorMiddleware` — throws `ApiError` on any non-2xx
 *   - `createRefreshMiddleware().wrap` — 401 → single-flight refresh → retry
 *
 * Sets `credentials: "include"` so the refresh cookie travels on cross-origin
 * calls to `api.hr.linkco.jo` from tenant subdomains (per CONTRACT.md auth flow).
 *
 * Middleware execution order is the order passed to `.use()`:
 *   onRequest:  auth → correlation → error(no-op) → refresh(no-op)
 *   onResponse: auth(no-op) → correlation → error(throws 4xx/5xx) → refresh(no-op)
 *
 * The 401 retry is NOT a middleware — it lives in the method-level `wrap`
 * because openapi-fetch v0.17's `onResponse` cannot retry a request.
 */
export function createApiClient({
  baseUrl,
  tokenProvider,
  setBillingWarning,
  fetch,
}: CreateApiClientOptions) {
  const fetchClientOptions: Parameters<typeof createFetchClient<paths>>[0] = {
    baseUrl,
    credentials: "include",
  };
  if (fetch) fetchClientOptions.fetch = fetch;
  const fetchClient = createFetchClient<paths>(fetchClientOptions);

  fetchClient.use(authMiddleware(tokenProvider));
  fetchClient.use(localeMiddleware);
  fetchClient.use(correlationMiddleware);
  fetchClient.use(errorMiddleware);
  // Billing middleware registers last so it sees the final (post error-normalization)
  // response. setBillingWarning is optional — Link Admin apps skip it entirely.
  if (setBillingWarning) {
    fetchClient.use(billingMiddleware({ setBillingWarning }));
  }

  const { wrap } = createRefreshMiddleware(tokenProvider);
  const w = wrap as unknown as Wrap;

  return {
    GET: w(fetchClient.GET),
    POST: w(fetchClient.POST),
    PUT: w(fetchClient.PUT),
    PATCH: w(fetchClient.PATCH),
    DELETE: w(fetchClient.DELETE),
    HEAD: w(fetchClient.HEAD),
    OPTIONS: w(fetchClient.OPTIONS),
    TRACE: w(fetchClient.TRACE),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

// ---------------------------------------------------------------------------
// uploadMultipart — XHR + progress path INSIDE the api-client boundary
// ---------------------------------------------------------------------------

/**
 * Per-project conventions §"No fetch() outside api-client", raw `XMLHttpRequest` is
 * permitted ONLY inside this package. File uploads need mid-flight progress
 * events (`xhr.upload.addEventListener("progress", …)`) which `fetch()` does
 * not expose — so multipart uploads route through this helper instead of
 * openapi-fetch's POST method.
 *
 * What we faithfully reproduce from the main client pipeline:
 *   - `Authorization: Bearer <access-token>` from `TokenProvider` (mirrors
 *     `authMiddleware`)
 *   - RFC 7807 error conversion to `ApiError` on non-2xx (mirrors
 *     `errorMiddleware`)
 *   - `credentials: "include"` so the refresh cookie travels on cross-origin
 *     POSTs to `api.hr.linkco.jo` from tenant subdomains
 *
 * What we deliberately DO NOT reproduce (not needed for a one-shot upload):
 *   - 401 → single-flight refresh → retry. If the upload 401s, let the hook
 *     surface the error; the next user action will trip the main client's
 *     refresh machinery. Retrying a partially-streamed upload after refresh
 *     is fiddly and the UX case is thin.
 *   - Correlation-ID Sentry/OTel span (correlationId is read FROM the
 *     response, not pre-sent — the response-side correlation still lands in
 *     the ApiError we throw).
 *
 * @param apiClient — the client returned by `createApiClient`. Used for its
 *   access-token and base URL shape; the helper reads `getAccessToken` from
 *   the same `TokenProvider` that's baked into the client.
 * @returns a resolved `T` on 2xx (parsed JSON), or throws `ApiError` on
 *   non-2xx / network failure.
 */
export interface UploadMultipartOptions {
  /** Absolute or path-prefixed URL (e.g. `/api/v1/employees/123/documents`). */
  path: string;
  /** Non-file FormData fields (all values must be plain strings). */
  fields: Record<string, string>;
  /** The file being uploaded. Keyed as `file` in the FormData body. */
  file: File;
  /**
   * Optional progress callback invoked with percentage (0..100, integer)
   * during upload. Only fires if the browser reports lengthComputable; on
   * chunked/unknown-length uploads we skip progress rather than guess.
   */
  onProgress?: (pct: number) => void;
  /**
   * Optional abort signal so the hook can cancel the upload when the
   * React component unmounts or the user hits Cancel.
   */
  signal?: AbortSignal;
  /**
   * Optional override of the FormData field name for the file itself.
   * Defaults to `"file"` (matches backend CC-07 multipart contract).
   */
  fileFieldName?: string;
}

interface UploadMultipartDeps {
  /** Bearer token — pass `tokenProvider.getAccessToken()`. */
  accessToken: string | null;
  /** Absolute origin (e.g. `http://localhost:8080` or `https://api.hr.linkco.jo`). */
  baseUrl: string;
}

/**
 * Resolve a request URL against the baseUrl. Absolute URLs pass through;
 * relative paths get the baseUrl prefix.
 */
function resolveUrl(baseUrl: string, path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const trimmedBase = baseUrl.replace(/\/+$/, "");
  const trimmedPath = path.replace(/^\/+/, "");
  return `${trimmedBase}/${trimmedPath}`;
}

/** Minimal shape that ApiError accepts — narrows on the wire payload. */
interface ParsedProblem {
  errorCode?: unknown;
  title?: unknown;
  status?: unknown;
  detail?: unknown;
  instance?: unknown;
  messageKey?: unknown;
  correlationId?: unknown;
}

/**
 * Convert an XHR non-2xx response into an `ApiError`. Mirrors the shape
 * that `errorMiddleware` produces for the fetch-based path so consumers
 * pattern-match uniformly.
 */
function apiErrorFromXhr(xhr: XMLHttpRequest): ApiError {
  let parsed: ParsedProblem = {};
  try {
    parsed = JSON.parse(xhr.responseText) as ParsedProblem;
  } catch {
    // Response was not JSON — fall through with empty parsed.
  }
  const correlationId =
    typeof parsed.correlationId === "string"
      ? parsed.correlationId
      : (xhr.getResponseHeader("x-correlation-id") ?? "mock-upload-no-correlation");
  const payload: ApiErrorPayload = {
    errorCode:
      typeof parsed.errorCode === "string"
        ? (parsed.errorCode as ErrorCode)
        : ("UNKNOWN_ERROR" as ErrorCode),
    title: typeof parsed.title === "string" ? parsed.title : `HTTP ${xhr.status}`,
    status: typeof parsed.status === "number" ? parsed.status : xhr.status,
    correlationId,
  };
  if (typeof parsed.detail === "string") payload.detail = parsed.detail;
  if (typeof parsed.instance === "string") payload.instance = parsed.instance;
  if (typeof parsed.messageKey === "string") payload.messageKey = parsed.messageKey;
  return new ApiError(payload);
}

/**
 * Multipart upload with progress events, staying inside the api-client
 * boundary. Consumers call this from hooks (e.g. `useUploadEmployeeDocument`
 * in `hooks/documents.ts`) — NEVER from component code.
 */
export function uploadMultipart<T>(
  deps: UploadMultipartDeps,
  opts: UploadMultipartOptions,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const url = resolveUrl(deps.baseUrl, opts.path);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.withCredentials = true; // matches createFetchClient's credentials: "include"

    if (deps.accessToken !== null) {
      xhr.setRequestHeader("Authorization", `Bearer ${deps.accessToken}`);
    }

    // Do NOT set `Content-Type` — the browser sets
    // `multipart/form-data; boundary=…` automatically when send(FormData).

    const fileField = opts.fileFieldName ?? "file";

    if (opts.onProgress !== undefined) {
      const cb = opts.onProgress;
      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable && e.total > 0) {
          cb(Math.round((e.loaded / e.total) * 100));
        }
      });
    }

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        let body: unknown = null;
        try {
          body = xhr.responseText ? (JSON.parse(xhr.responseText) as unknown) : null;
        } catch {
          body = null;
        }
        // Fire a final 100% signal — the last progress event may lag or
        // be missed for small files; this guarantees completion.
        opts.onProgress?.(100);
        resolve(body as T);
      } else {
        reject(apiErrorFromXhr(xhr));
      }
    });

    xhr.addEventListener("error", () => {
      reject(
        new ApiError({
          errorCode: "UNKNOWN_ERROR" as ErrorCode,
          title: "Network error during upload",
          status: 0,
          correlationId: "mock-upload-network-error",
        }),
      );
    });
    xhr.addEventListener("abort", () => {
      reject(
        new ApiError({
          errorCode: "UNKNOWN_ERROR" as ErrorCode,
          title: "Upload aborted",
          status: 0,
          correlationId: "mock-upload-aborted",
        }),
      );
    });

    // Wire the AbortSignal if supplied.
    if (opts.signal !== undefined) {
      const signal = opts.signal;
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    const form = new FormData();
    for (const [k, v] of Object.entries(opts.fields)) {
      form.append(k, v);
    }
    form.append(fileField, opts.file);
    xhr.send(form);
  });
}
